import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';

interface ContactPageProps {
  onNavigate: (page: string) => void;
}

export function ContactPage({ onNavigate }: ContactPageProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    title: '',
    phone: '',
    projectVolume: '',
    inquiry: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch('https://formspree.io/f/mgolvgpy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          company: formData.company,
          projectVolume: formData.projectVolume,
          message: formData.message,
        }),
      });

      if (!res.ok) {
        throw new Error('Form submission failed');
      }

      setSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        title: '',
        phone: '',
        projectVolume: '',
        inquiry: '',
        message: '',
      });
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2A3B4C] to-[#3E5365] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl mb-6">Contact Karyva Team</h1>
              <p className="text-xl text-[#D5D9DD] leading-relaxed">
                Ready to transform your project portfolio management? Get in touch with our team
                to discuss your organization's needs and see how Karyva can help.
              </p>
            </div>
            <div className="hidden md:block">
              <img 
                src="https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjB3b3Jrc3BhY2V8ZW58MXx8fHwxNzY3MDY0NDE3fDA&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Modern Office"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-8">
                <div>
                  <h2 className="text-3xl text-[#2A3B4C] mb-6">Get In Touch</h2>
                  <p className="text-[#556B7C] leading-relaxed mb-8">
                    Our team is ready to answer your questions and help you discover how Karyva
                    can drive project excellence at your organization.
                  </p>
                </div>

                <div className="space-y-6">
                  <ContactInfo
                    icon={Mail}
                    title="Email"
                    value="sales@karyva.com"
                    link="mailto:sales@karyva.com"
                  />
                  <ContactInfo
                    icon={Phone}
                    title="Phone"
                    value="+60 10-915 6516"
                    link="tel:+15551234567"
                  />
                  <ContactInfo
                    icon={MapPin}
                    title="Headquarters"
                    value="Global (Cloud-Native)"
                  />
                </div>

                <div className="bg-[#E8E9E9] rounded-lg p-6 border border-[#C4CDD5]">
                  <h3 className="text-lg text-[#2A3B4C] mb-3">What Happens Next?</h3>
                  <ol className="space-y-3 text-sm text-[#556B7C]">
                    <li className="flex gap-3">
                      <span className="text-[#DAA520] font-bold">1.</span>
                      <span>We'll respond within 24 hours</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-[#DAA520] font-bold">2.</span>
                      <span>Schedule a discovery call to understand your needs</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-[#DAA520] font-bold">3.</span>
                      <span>Provide a customized demo of Karyva</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-[#DAA520] font-bold">4.</span>
                      <span>Discuss pricing and implementation timeline</span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#E8E9E9] rounded-lg border border-[#C4CDD5] p-8">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-[#DAA520] rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl text-[#2A3B4C] mb-3">Thank You!</h3>
                    <p className="text-[#556B7C]">
                      We've received your message and will get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div>
                      <label className="block text-sm text-[#2A3B4C] mb-2">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                        className="w-full px-4 py-3 bg-white border border-[#C4CDD5] rounded-lg text-[#2A3B4C] focus:outline-none focus:ring-2 focus:ring-[#DAA520]"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm text-[#2A3B4C] mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your.email@company.com"
                        className="w-full px-4 py-3 bg-white border border-[#C4CDD5] rounded-lg text-[#2A3B4C] focus:outline-none focus:ring-2 focus:ring-[#DAA520]"
                      />
                    </div>

                    {/* Company */}
                    <div>
                      <label className="block text-sm text-[#2A3B4C] mb-2">
                        Company <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        placeholder="Your organization"
                        className="w-full px-4 py-3 bg-white border border-[#C4CDD5] rounded-lg text-[#2A3B4C] focus:outline-none focus:ring-2 focus:ring-[#DAA520]"
                      />
                    </div>

                    {/* Project Volume */}
                    <div>
                      <label className="block text-sm text-[#2A3B4C] mb-2">
                        Project Volume
                      </label>
                      <select
                        name="projectVolume"
                        value={formData.projectVolume}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-[#C4CDD5] rounded-lg text-[#2A3B4C] focus:outline-none focus:ring-2 focus:ring-[#DAA520]"
                      >
                        <option value="">Select range</option>
                        <option value="1-10">1-10 projects</option>
                        <option value="11-50">11-50 projects</option>
                        <option value="51-100">51-100 projects</option>
                        <option value="100+">100+ projects</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm text-[#2A3B4C] mb-2">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        placeholder="Tell us about your organization's needs and what you're looking to accomplish with Karyva..."
                        className="w-full px-4 py-3 bg-white border border-[#C4CDD5] rounded-lg text-[#2A3B4C] focus:outline-none focus:ring-2 focus:ring-[#DAA520] resize-none"
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <div className="flex items-center justify-between pt-4">
                      <div className="flex flex-col items-start gap-1">
                        <p className="text-sm text-[#556B7C]">
                          <span className="text-red-500">*</span> Required fields
                        </p>
                        {error && (
                          <p className="text-xs text-red-500">
                            {error}
                          </p>
                        )}
                      </div>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="bg-[#DAA520] hover:bg-[#B8860B] disabled:opacity-70 disabled:cursor-not-allowed text-white px-8 py-6 text-lg"
                      >
                        {submitting ? 'Sending...' : 'Send Message'}
                        {!submitting && <Send className="ml-2 w-5 h-5" />}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-20 bg-[#E8E9E9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <InfoCard
              title="Sales Inquiries"
              description="Interested in Karyva for your organization? Our sales team will work with you to understand your needs and provide a customized solution."
              contact="sales@karyva.com"
            />
            <InfoCard
              title="Support"
              description="Existing customers can reach our 24/7 enterprise support team for technical assistance and platform guidance."
              contact="support@karyva.com"
            />
            <InfoCard
              title="Careers"
              description="Looking to join our team? Check out our open positions and reach out to our talent team."
              contact="careers@karyva.com"
              action={() => onNavigate('careers')}
              actionLabel="View Open Positions"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactInfo({ icon: Icon, title, value, link }: { icon: any; title: string; value: string; link?: string }) {
  const content = (
    <>
      <div className="w-10 h-10 bg-[#DAA520] rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-sm text-[#556B7C] mb-1">{title}</div>
        <div className="text-[#2A3B4C]">{value}</div>
      </div>
    </>
  );

  if (link) {
    return (
      <a href={link} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
        {content}
      </a>
    );
  }

  return <div className="flex items-center gap-3">{content}</div>;
}

function InfoCard({
  title,
  description,
  contact,
  action,
  actionLabel,
}: {
  title: string;
  description: string;
  contact: string;
  action?: () => void;
  actionLabel?: string;
}) {
  return (
    <div className="bg-white rounded-lg p-6 border border-[#C4CDD5]">
      <h3 className="text-xl text-[#2A3B4C] mb-3">{title}</h3>
      <p className="text-[#556B7C] mb-4 leading-relaxed text-sm">{description}</p>
      <a href={`mailto:${contact}`} className="text-[#DAA520] hover:text-[#B8860B] text-sm block mb-3">
        {contact}
      </a>
      {action && actionLabel && (
        <Button
          onClick={action}
          variant="outline"
          size="sm"
          className="border-[#DAA520] text-[#DAA520] hover:bg-[#FFF9E6] w-full"
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
}